import styled from "styled-components";
import CAMERA_SVG from "assets/images/camera.svg";
import SAVE_SVG from "assets/images/save.svg";
import CANCEL_SVG from "assets/images/cancel.svg";
import useStudentActivity from "features/teacher/hooks/student/useStudentActivity";
import Modal from "shared/components/Modal";
import Loading from "shared/components/Loading";
import { useNavigate } from "react-router-dom";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface StudentActivityProps{
    studentUid:string,
    onCancelClick:React.MouseEventHandler,
}

const StudentActivity = ({onCancelClick, studentUid}:StudentActivityProps) => {
    const naviate = useNavigate();
    const {inputs, onChange, videoInputRef, onVideoInputClick, onFileInput, videoURL, onSaveClick,
        showNullModal, setShowNullModal, uploadProgerss, showLoading,
        isSucceed, setIsSucceed, isError, setIsError
    } = useStudentActivity(studentUid);
    return(
        <Container>
            {showLoading ? <Loading title={"동영상 업로드 중"} subTitle={Math.floor(uploadProgerss).toString() + "%"} progress={uploadProgerss}/> : null}
            <Modal title="SUCCESS" content="등록에 성공하였습니다" isOpen={isSucceed} onClose={() => {naviate(0)}}/>
            <Modal title="ERROR" content="영상 업로드중 오류가 발생하였습니다" isOpen={isError} onClose={() => setIsError(false)}/>
            <Modal title="ERROR" content="제목과 영상을 지정하여야 합니다" isOpen={showNullModal} onClose={() => setShowNullModal(false)}/>
            <Content>
                <CancelButton onClick={onCancelClick}>
                    <CancelIcon src={CANCEL_SVG} alt="cancel"/>
                </CancelButton>
                <Header>
                    <input type="file" style={{display:"none"}} accept="video/*" ref={videoInputRef} onChange={onFileInput}/>
                    <InputTitle placeholder="제목을 입력해 주세요" name="title" value={inputs.title} onChange={onChange}/>
                </Header>
                <ToolWrap>
                    <ImageButton onClick={onVideoInputClick}>
                        <Logo src={CAMERA_SVG} alt="camera"/>
                        <Name>동영상</Name>
                    </ImageButton>
                    <Description>*동영상은 하나만 업로드가 가능합니다</Description>
                </ToolWrap>
                <MainContent>
                    {videoURL && <VideoPreview src={videoURL} controls/>}
                    <TextContent name="contents" onChange={onChange} value={inputs.contents} placeholder="내용을 입력해 주세요"/>
                    <ButtonWrap>
                        <Button onClick={onSaveClick}>
                            <ButtonLogo src={SAVE_SVG} alt="save"/>
                            <ButtonName>저장하기</ButtonName>
                        </Button>
                    </ButtonWrap>
                </MainContent>
            </Content>
        </Container>
    )
}

const Container = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(6px);
    z-index: 100;
    overflow-y: auto;
    padding: 32px 20px;
`

const Content = styled.div`
    position: relative;
    width: 720px;
    max-width: 100%;
    margin: auto;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 20px;
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 28px 28px 24px;
    overflow: auto;
    max-height: calc(100vh - 64px);
    ${scrollbar}
`

const CancelButton = styled.button`
    position: absolute;
    right: 20px;
    top: 20px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    transition: background-color 0.2s;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
`

const CancelIcon = styled.img`
    width: 20px;
    height: 20px;
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const InputTitle = styled.input`
    background: none;
    border: none;
    border-bottom: 2px solid #E0E0E0;
    color: #1A1A1A;
    font-size: 24px;
    font-weight: 600;
    padding: 12px 0;
    transition: border-color 0.2s;
    width: 100%;

    &:focus {
        outline: none;
        border-color: #DF0000;
    }

    &::placeholder {
        color: #999;
    }
`

const ToolWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const ImageButton = styled.button`
    align-items: center;
    background-color: #F8F9FA;
    border: 2px dashed #E0E0E0;
    border-radius: 16px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px;
    width: 120px;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
        background-color: #F0F0F0;
        border-color: #DF0000;
    }
`

const Description = styled.span`
    color: #666;
    font-size: 14px;
    font-weight: 400;
`

const Logo = styled.img`
    width: 32px;
    height: 32px;
`

const Name = styled.span`
    color: #666;
    font-size: 16px;
    font-weight: 500;
`

const MainContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 300px;
    max-height: 500px;
    overflow-y: auto;
    padding-right: 8px;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #F8F9FA;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #E0E0E0;
        border-radius: 4px;

        &:hover {
            background: #D0D0D0;
        }
    }
`

const VideoPreview = styled.video`
    width: 100%;
    border-radius: 12px;
    background: #000;
`

const TextContent = styled.textarea`
    color: #1A1A1A;
    font-size: 16px;
    font-weight: 400;
    width: 100%;
    height: 300px;
    line-height: 1.6;
    padding: 16px;
    resize: none;
    border: 2px solid #E0E0E0;
    border-radius: 12px;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: #DF0000;
    }

    &::placeholder {
        color: #999;
    }
`

const ButtonWrap = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 16px;
`

const Button = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #DF0000;
    border: none;
    border-radius: 8px;
    color: #fff;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 18px;

    &:hover {
        background: #B91C1C;
    }
`

const ButtonLogo = styled.img`
    width: 24px;
    height: 24px;
`

const ButtonName = styled.span`
    font-weight: 500;
`

export default StudentActivity;